import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { dashboardListQuery } from './dashboard-list-query';

/**
 * The chevron beside a dashboard's title, listing every dashboard the viewer
 * can open — their own and those shared with them — to jump straight to
 * another.
 *
 * Each entry is a LINK, not a handler: it opens in a new tab like any other
 * link, and it goes through the router, so the page's unsaved-layout guard
 * still stops a switch away from an edit in progress.
 *
 * The next dashboard opens on its OWN period and filters. They are this
 * dashboard's, and a filter on a dimension the next one's tiles do not share
 * would ride along in its link and be dropped or refused there.
 */
export function SwitchDashboardMenu({ currentId }: { currentId: string }) {
  // ON MOUNT, because whether the chevron is drawn at all depends on it: with
  // only one dashboard to open there is nowhere to switch to, and a menu that
  // lists just the page you are on is a dead end. Shares the index's cache
  // entry, so arriving from there costs nothing.
  const list = useQuery(dashboardListQuery());
  const rows = list.data?.data ?? [];
  const total = list.data?.meta.total ?? 0;

  // Nothing until the list says there is somewhere else to go — including
  // while it loads and when it fails. A chevron that appears and then vanishes
  // is worse than one that appears late.
  if (total < 2) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          // SMALLER than the pencil's box, so the chevron hugs the title it
          // belongs to instead of floating off it.
          className="size-7 shrink-0 text-muted-foreground"
          aria-label="Switch dashboard"
          title="Switch dashboard"
        >
          <ChevronDown className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      {/* CAPPED at 20rem, and never past the room left in the viewport — the
          primitive's own cap, kept by folding it into the `min()` rather than
          replacing it. The list scrolls inside. */}
      <DropdownMenuContent
        align="start"
        className="max-h-[min(20rem,var(--radix-dropdown-menu-content-available-height))] w-80"
      >
        {rows.map((d) => {
          const current = d.id === currentId;
          const body = (
            <>
              <span className="min-w-0 flex-1">
                <span
                  data-slot="dashboard-name"
                  className="block truncate font-medium"
                  title={d.name}
                >
                  {d.name}
                </span>
                {d.description && (
                  <span
                    data-slot="dashboard-description"
                    className="line-clamp-2 text-muted-foreground text-xs"
                  >
                    {d.description}
                  </span>
                )}
              </span>
              {current && <Check className="mt-0.5 size-4 text-foreground" aria-hidden="true" />}
            </>
          );
          // The one on screen only closes the menu. As a link it would reload
          // this dashboard WITHOUT its search, throwing away the period and
          // filters the reader has set — the opposite of a no-op.
          return current ? (
            <DropdownMenuItem
              key={d.id}
              aria-current="page"
              data-multiline
              className="items-start bg-accent/50"
            >
              {body}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem key={d.id} asChild data-multiline className="items-start">
              <Link to="/dashboards/$dashboardId" params={{ dashboardId: d.id }}>
                {body}
              </Link>
            </DropdownMenuItem>
          );
        })}
        {/* CUT OFF, said so: past the server's ceiling the rest are not here,
            and a missing dashboard must not read as a deleted one. */}
        {total > rows.length && (
          <p className="px-2 py-1.5 text-muted-foreground text-xs">
            Showing {rows.length} of {total} dashboards.
          </p>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
